/**
 * ヤギプライム紹介キャンペーン（招待する側）の API クライアント。
 *
 * 画面から axios を生で叩かず、エンドポイント定義と Body の組み立てをここに集約する
 * （CLAUDE.md: HTTP / API Client Constraints, Infra Plugin Access Constraints）。
 * 新規エンドポイントのため **リクエスト・レスポンスとも JSON**（FormData は使わない）。
 *
 * 付与・累積・成立判定はサーバー側が正本。フロントは返ってきた値を表示するだけで、
 * 日数をここで計算しない。サーバー側実装は別担当のため、未実装の間は各関数が null を
 * 返す（呼び出し側は紹介URLを出さずに従来 UI へフォールバックする）。
 */
import axios from 'axios'

const ENDPOINT_PREFIX = process.env.VUE_APP_ENDPOINT_PREFIX || '/'

const REQUEST_TIMEOUT_MS = 15000

const url = (path: string) => `${ENDPOINT_PREFIX}${path}`

/** 紹介者の固有コード。1ユーザー1コードで、再訪しても同じ値が返る想定 */
export type ReferralCodeResponse = {
  referral_code: string
}

/**
 * 紹介状況。click_count / signup_count は BQ 集計側の都合で欠ける場合があるため任意。
 * 成立数と獲得日数は表示の主役なので必須項目として扱う。
 */
export type ReferralStatusResponse = {
  referral_success_count: number
  granted_days_total: number
  click_count?: number | null
  signup_count?: number | null
  /**
   * 被紹介者として紐付いているか。フロントは登録とログインを同じ経路で
   * 処理していて referral_code の有無では区別できないため、
   * 「紹介が成立したか」を知る唯一の手段がこのフラグ。
   */
  is_referee?: boolean
  /** 被紹介者として次の加入で無料になる日数。0 なら特典を訴求してはいけない */
  referee_pending_days?: number
  /**
   * 紹介する側になれるか。**シェア導線の出し分けはこれだけで決める。**
   * 「未加入か」で判定すると、期間中に加入した紹介者（サーバーは対象のまま
   * 扱い、成立分で trial_end を伸ばす）から導線が消える。
   */
  can_refer?: boolean
  /** 紹介成立が上限（12件）に達したか */
  referral_cap_reached?: boolean
  /**
   * 次の加入で trial_end に乗る合計日数（被紹介者分＋紹介者として溜まった分）。
   * 加入画面に出す「N日間無料」はこちら。
   */
  pending_days_total?: number
}

async function getJson<T>(path: string): Promise<T | null> {
  try {
    const { data } = await axios.get<T>(url(path), {
      timeout: REQUEST_TIMEOUT_MS,
      headers: { Accept: 'application/json' }
    })
    return data ?? null
  } catch (error) {
    // 未発行・サーバー側未実装はいずれも「紹介URLを出さない」で正しく畳める
    console.error(error)
    return null
  }
}

/** 紹介コードを取得する（未発行ならサーバー側で発行して返す想定） */
export function fetchReferralCode(uid: string) {
  return getJson<ReferralCodeResponse>(`yagiprime/referral/code/${uid}/`)
}

/** 紹介状況（クリック・登録・成立・獲得日数） */
export function fetchReferralStatus(uid: string) {
  return getJson<ReferralStatusResponse>(`yagiprime/referral/status/${uid}/`)
}

/**
 * ログイン済みのまま紹介URLで来た会員を、その場で被紹介者として紐付ける。
 *
 * 通常の紐付けは login の POST に載せた referral_code で成立するが、
 * すでにログインしているブラウザでは認証イベントが起きず login が飛ばない。
 * その経路を埋めるための呼び出しで、返り値は紐付け後の紹介状況。
 * 失敗（対象外・通信断）は null で、呼び出し側は従来どおり status を見る。
 *
 * ⚠️ Firebase の ID トークンが必須。紐付けは**取り消せない**（一度被紹介者に
 * なるとその会員は紹介する側に戻れない）ので、サーバーはパスの uid だけでは
 * 受け付けず、トークンの uid と一致することを確かめる。
 */
export async function attachReferral(
  uid: string,
  code: string,
  idToken: string
): Promise<ReferralStatusResponse | null> {
  if (!idToken) return null
  try {
    const { data } = await axios.post<ReferralStatusResponse>(
      url(`yagiprime/referral/attach/${uid}/`),
      { code },
      {
        timeout: REQUEST_TIMEOUT_MS,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`
        }
      }
    )
    return data ?? null
  } catch (error) {
    console.error(error)
    return null
  }
}

/**
 * 紹介URL(/r/<code>)の着地を記録する。
 *
 * KPI「紹介URLからの流入 10,000UU/月」の実体。サーバーは
 * `visitor_key` の distinct を UU として数えるので必ず一緒に送る。
 * 実在しないコード・同一 visitor_key の同日重複はサーバー側が 204 で捨てる。
 *
 * 失敗しても着地そのものは止めない（計測が落ちても導線は通す）ので
 * 例外は投げず、記録できたかどうかだけ返す。
 */
export async function postReferralClick(
  code: string,
  visitorKey: string
): Promise<boolean> {
  try {
    const response = await axios.post(
      url('yagiprime/referral/click/'),
      { code, visitor_key: visitorKey },
      {
        timeout: REQUEST_TIMEOUT_MS,
        headers: { 'Content-Type': 'application/json' }
      }
    )
    return response.status === 200
  } catch (error) {
    console.error(error)
    return false
  }
}
